import { FormikProvider, useFormik } from 'formik'

import { useReselector } from '@/utils/sharedHooks'
import { getFinancialProfile } from '@/selectors/user.selectors'
import {
  UPDATE_FINANCIAL_PROFILE_KEY,
  updateFinancialProfile,
} from '@/actions/financialProfileActions'
import {
  getProjectedW2Income,
  TAX_PROFILE_STEP,
  TaxesProfileFlowAnswerProps,
  useTaxProfileSteps,
} from './helpers'
import { dollarsToCents, formatCurrency } from '@/utils/currencyHelpers'
import { getFetchError } from '@/reducers/fetch'
import { useAnalyticsTrack } from '@/features/amplitude'
import { useAppDispatch } from '@/utils/typeHelpers'

import { Alert, Form, Text } from '@/ui'
import { TypographyStyles } from '@/styles'
import { TaxProfileFormFooter } from './TaxProfileFormFooter'
import {
  TaxProfileAccordions,
  useTaxProfileAccordionCopy,
} from './TaxProfileAccordions'
import { W2IncomeWarning } from './W2IncomeWarning'
import {
  PaystubVsEstimateAccordion,
  PaystubAccordionContent,
} from './PaystubVsEstimateAccordion'
import { QuarterlyTaxEstimateDetail } from '@/features/admin/quarterly-tax-estimate-details/quarterlyTaxEstimateDetails.slice'
import { selectActiveQuarterlyTaxEstimateDetails } from '@/features/admin/quarterly-tax-estimate-details/quarterlyTaxEstimateDetails.selector'

// TODO: Migrate to V2
import {
  FormikLabelError,
  FormikRadioToggleButton,
  getFieldName,
  makeNumberSchema,
  makeReqBoolSchema,
} from '@/base'
import { LabelDescription } from '@/componentsV1/BaseComponents/Input'

const spouseW2IncomeCopy = (
  activeQuarterDetails?: QuarterlyTaxEstimateDetail
) => {
  switch (activeQuarterDetails?.taxQuarter) {
    case '1':
      return "Enter income earned from your spouse's W-2 job(s) for pay periods starting January 1st through February 28th. You'll find this on the first paycheck they received in March."
    case '2':
      return "Enter income earned from your spouse's W-2 job(s) for pay periods starting January 1st through April 30th. You'll find this on the first paycheck they received in May."
    case '3':
      return "Enter income earned from your spouse's W-2 job(s) for pay periods starting January 1st through July 31st. You'll find this on the first paycheck they received in August."
    case '4':
      return "Enter income earned from your spouse's W-2 job(s) for pay periods starting January 1st through November 30st. You'll find this on the first paycheck they received in December."
    // should never happen, but fall back to generic copy
    default:
      return 'Please enter income from your most recent paystub. Do not enter your net profit.'
  }
}

export const SpouseW2Form = ({
  formFlowAnswers,
  setFormFlowAnswers,
}: TaxesProfileFlowAnswerProps) => {
  const dispatch = useAppDispatch()
  const track = useAnalyticsTrack()
  const fp = useReselector(getFinancialProfile)
  const error = useReselector(
    getFetchError,
    UPDATE_FINANCIAL_PROFILE_KEY(fp?.id)
  )
  const { goToPreviousStep, goToNextStep } = useTaxProfileSteps(
    TAX_PROFILE_STEP.spouseW2
  )

  const someIncomeEntered =
    formFlowAnswers.spouseWIncome !== null ||
    formFlowAnswers.estimatedAnnualSpouseW2Income !== null

  const formik = useFormik({
    enableReinitialize: true,
    validateOnMount: true,
    initialValues: {
      spouseHasWIncome: someIncomeEntered ? true : undefined,
      enterSpouseW2: someIncomeEntered
        ? formFlowAnswers.spouseWIncome !== null
        : undefined,
      spouseWIncome: formFlowAnswers.spouseWIncome,
      estimatedAnnualSpouseW2Income:
        formFlowAnswers.estimatedAnnualSpouseW2Income,
    },
    onSubmit: async ({
      spouseHasWIncome,
      spouseWIncome,
      enterSpouseW2,
      estimatedAnnualSpouseW2Income,
    }) => {
      const res = await dispatch(
        updateFinancialProfile(fp?.id, {
          spouseWIncomeInCents:
            spouseHasWIncome && enterSpouseW2
              ? dollarsToCents(spouseWIncome)
              : null,
          estimatedAnnualSpouseW2IncomeInCents:
            spouseHasWIncome && !enterSpouseW2
              ? dollarsToCents(estimatedAnnualSpouseW2Income)
              : null,
          // User will skip the withholding page if answered no - clear these values in this case
          ...(!spouseHasWIncome && {
            spouseFederalWithholdingInCents: null,
            spouseStateWithholdingInCents: null,
          }),
        })
      )

      if (res) {
        const newAnswers = {
          ...formFlowAnswers,
          spouseWIncome,
          estimatedAnnualSpouseW2Income,
        }

        if (spouseHasWIncome) {
          if (enterSpouseW2) {
            newAnswers.estimatedAnnualSpouseW2Income = null
          } else {
            newAnswers.spouseWIncome = null
          }
        } else {
          newAnswers.spouseWIncome = null
          newAnswers.estimatedAnnualSpouseW2Income = null
          newAnswers.spouseFederalWithholding = null
          newAnswers.spouseStateWithholding = null
        }
        setFormFlowAnswers(newAnswers)
        track('spouse w2 income submitted', {
          spouse_has_w2_income: spouseHasWIncome ?? false,
          enter_spouse_paystub_w2: Boolean(enterSpouseW2),
          spouse_w2_income_in_cents:
            spouseHasWIncome && enterSpouseW2
              ? dollarsToCents(spouseWIncome)
              : null,
          estimated_annual_spouse_w2_income_in_cents:
            spouseHasWIncome && !enterSpouseW2
              ? dollarsToCents(estimatedAnnualSpouseW2Income)
              : null,
          user_id: fp?.userId ?? null,
        })
        goToNextStep(res)
      }
    },
  })

  const { values, isSubmitting, isValid, submitForm, validateForm, touched } =
    formik

  const activeQuarterDetails = useReselector(
    selectActiveQuarterlyTaxEstimateDetails
  )

  const spouseW2IncomeSubcopy = spouseW2IncomeCopy(activeQuarterDetails)

  const projectedSpouseW2Income = getProjectedW2Income({
    ytdIncomeInDollars: values.spouseWIncome,
    quarterDetails: activeQuarterDetails,
  })

  const faqCopy = useTaxProfileAccordionCopy()

  return (
    <FormikProvider value={formik}>
      <div className="w-full space-y-6">
        <Text
          className="max-w-3xl mx-auto text-center"
          variant={TypographyStyles.DISPLAY_SM}
        >
          Your spouse’s W-2 income
        </Text>

        <Text
          className="max-w-3xl mx-auto text-center"
          variant={TypographyStyles.BODY_LG}
        >
          We need to know your spouse’s total income for the year to calculate
          your tax bracket.
        </Text>

        {/* Error */}
        {error && (
          <Alert
            className="max-w-3xl mx-auto"
            variant="error"
            description={error.message}
          />
        )}

        {/* Question label */}
        <div className="max-w-2xl mx-auto">
          <FormikLabelError
            name={getFieldName<typeof values>('spouseHasWIncome')}
            schema={makeReqBoolSchema()}
            label="Does your spouse have a W-2 job?"
            description="A W-2 job is where a person is considered an employee and receives a W-2 tax form each year that reports their income and tax withholdings."
          />
        </div>

        {/* Toggle buttons */}
        <div className="max-w-2xl mx-auto flex flex-col sm:flex-row justify-center gap-4">
          <div className="sm:w-1/2 w-full">
            <FormikRadioToggleButton
              name={getFieldName<typeof values>('spouseHasWIncome')}
              value
              fullWidth
              onClick={validateForm}
            >
              Yes
            </FormikRadioToggleButton>
          </div>
          <div className="sm:w-1/2 w-full">
            <FormikRadioToggleButton
              name={getFieldName<typeof values>('spouseHasWIncome')}
              value={false}
              fullWidth
              onClick={validateForm}
            >
              No
            </FormikRadioToggleButton>
          </div>
        </div>

        {/* If spouse has W-2 income */}
        {values.spouseHasWIncome && (
          <div className="space-y-6">
            {/* How to enter income */}
            <div className="max-w-2xl mx-auto space-y-4">
              <FormikLabelError
                name={getFieldName<typeof values>('enterSpouseW2')}
                label="How would you like to enter income?"
                description="Click on “How should I choose?” below if you need help deciding how to enter income."
                schema={makeReqBoolSchema()}
              />

              <div className="flex flex-col sm:flex-row gap-4">
                <FormikRadioToggleButton
                  name={getFieldName<typeof values>('enterSpouseW2')}
                  value
                  fullWidth
                >
                  Year-to-date from paystub
                </FormikRadioToggleButton>
                <FormikRadioToggleButton
                  name={getFieldName<typeof values>('enterSpouseW2')}
                  value={false}
                  fullWidth
                >
                  Estimated income
                </FormikRadioToggleButton>
              </div>
            </div>

            {/* If entering YTD income */}
            {values.enterSpouseW2 && (
              <div className="max-w-2xl mx-auto space-y-4">
                <LabelDescription
                  required
                  label="Spouse's year-to-date W-2 Gross Income"
                  description={spouseW2IncomeSubcopy}
                />

                <div className="flex flex-col lg:flex-row gap-4 items-center">
                  <div className="w-1/2">
                    <Form.Input
                      name={getFieldName<typeof values>('spouseWIncome')}
                      required
                      componentType="currency"
                      placeholder="$"
                      schema={makeNumberSchema({
                        allowedDecimals: 2,
                        greaterThanZero: true,
                      })}
                    />
                  </div>
                  <div className="w-1/2">
                    {projectedSpouseW2Income && (
                      <Alert
                        className="w-full p-4"
                        title={`${formatCurrency(projectedSpouseW2Income, {
                          hideDecimalsIfZero: true,
                        })} projected annually`}
                      />
                    )}
                  </div>
                </div>

                <W2IncomeWarning
                  inputTouched={touched.spouseWIncome}
                  w2Income={values.spouseWIncome}
                />
              </div>
            )}

            {/* If entering estimated income */}
            {values.enterSpouseW2 === false && (
              <div className="max-w-2xl mx-auto">
                <Form.Input
                  name={getFieldName<typeof values>(
                    'estimatedAnnualSpouseW2Income'
                  )}
                  required
                  componentType="currency"
                  schema={makeNumberSchema({
                    allowedDecimals: 2,
                    greaterThanZero: true,
                  })}
                  placeholder="$"
                  label={`${activeQuarterDetails?.taxYear} Annual gross income for your spouse’s W-2 job `}
                  description="Please include the total income from all their W-2 job(s)."
                />
              </div>
            )}

            {/* Paystub vs Estimate Accordion */}
            <div className="max-w-3xl mx-auto">
              <PaystubVsEstimateAccordion
                content={PaystubAccordionContent.spouse}
              />
            </div>
          </div>
        )}

        {/* FAQs */}
        <TaxProfileAccordions
          faqs={[
            faqCopy.whyShareSpouseIncome,
            faqCopy.whatIsW2Job,
            faqCopy.howDoIEstimate,
            faqCopy.whatDoesYTDMeanSpouse,
            faqCopy.whatIsGrossIncome,
          ]}
        />

        {/* Footer */}
        <TaxProfileFormFooter
          submitOrContinue={submitForm}
          goToPreviousStep={goToPreviousStep}
          continueDisabled={!isValid || isSubmitting}
          loading={isSubmitting}
        />
      </div>
    </FormikProvider>
  )
}
